import React, { useEffect, useRef, useState } from 'react';
import type { TourState, TourStep } from './TourContext';

const PAD = 10;

interface Props {
  state: TourState;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
}

export function TourOverlay({ state, onNext, onPrev, onClose }: Props) {
  const step = state.steps[state.currentIndex];
  const [rect, setRect] = useState<DOMRect | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    // Let the step bring its target on screen first (e.g. switch a tab);
    // the target may only exist after React re-renders, so retry for a
    // few frames before falling back to the centred tooltip.
    step!.before?.();
    let retries = 0;
    function measure() {
      const el = document.querySelector(step!.target);
      if (!el && retries < 30) {
        retries += 1;
        rafRef.current = requestAnimationFrame(measure);
      }
      setRect(el ? el.getBoundingClientRect() : null);
      // scroll element into view if not visible
      if (el) {
        const r = el.getBoundingClientRect();
        const inView = r.top >= 0 && r.bottom <= window.innerHeight;
        if (!inView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [step]);

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const cx = rect ? rect.left - PAD : -9999;
  const cy = rect ? rect.top - PAD : -9999;
  const cw = rect ? rect.width + PAD * 2 : 0;
  const ch = rect ? rect.height + PAD * 2 : 0;
  const cr = step!.spotlightShape === 'circle' ? Math.max(cw, ch) / 2 : 10;

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9000, pointerEvents: 'auto' }}
      role="dialog"
      aria-modal="true"
      aria-label={`Tour: ${step!.title} (step ${state.currentIndex + 1} of ${state.steps.length})`}
    >
      {/* SVG spotlight mask */}
      <svg
        width={vw}
        height={vh}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
        aria-hidden="true"
      >
        <defs>
          <mask id="tour-mask">
            <rect width={vw} height={vh} fill="white" />
            {step!.spotlightShape === 'circle' ? (
              <circle cx={cx + cw / 2} cy={cy + ch / 2} r={cr} fill="black" />
            ) : (
              <rect x={cx} y={cy} width={cw} height={ch} rx={cr} fill="black" />
            )}
          </mask>
        </defs>
        <rect width={vw} height={vh} fill="rgba(0,0,0,0.55)" mask="url(#tour-mask)" />
      </svg>

      {/* Click backdrop to close */}
      <div style={{ position: 'absolute', inset: 0 }} role="presentation" onClick={onClose} />

      {/* Pulse ring */}
      {rect && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            left: cx - 4,
            top: cy - 4,
            width: cw + 8,
            height: ch + 8,
            borderRadius: step!.spotlightShape === 'circle' ? '50%' : 14,
            border: '2px solid var(--accent)',
            animation: 'tour-pulse 1.8s ease-out infinite',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      )}

      {/* Tooltip */}
      {rect && (
        <TourTooltip
          step={step!}
          rect={rect}
          totalSteps={state.steps.length}
          currentIndex={state.currentIndex}
          vw={vw}
          vh={vh}
          onNext={onNext}
          onPrev={onPrev}
          onClose={onClose}
        />
      )}

      {/* If target not found, show centered fallback tooltip */}
      {!rect && (
        <TourTooltip
          step={step!}
          rect={new DOMRect(vw / 2 - 150, vh / 2 - 60, 300, 120)}
          totalSteps={state.steps.length}
          currentIndex={state.currentIndex}
          vw={vw}
          vh={vh}
          onNext={onNext}
          onPrev={onPrev}
          onClose={onClose}
        />
      )}
    </div>
  );
}

interface TooltipProps {
  step: TourStep;
  rect: DOMRect;
  totalSteps: number;
  currentIndex: number;
  vw: number;
  vh: number;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
}

const TOOLTIP_W = 300;
const TOOLTIP_H = 180; // approximate

function TourTooltip({ step, rect, totalSteps, currentIndex, vw, vh, onNext, onPrev, onClose }: TooltipProps) {
  const pad = PAD + 12;

  let top: number;
  let left: number;

  switch (step.placement) {
    case 'bottom':
      top = rect.bottom + PAD + pad;
      left = rect.left + rect.width / 2 - TOOLTIP_W / 2;
      break;
    case 'top':
      top = rect.top - PAD - pad - TOOLTIP_H;
      left = rect.left + rect.width / 2 - TOOLTIP_W / 2;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - TOOLTIP_H / 2;
      left = rect.right + PAD + pad;
      break;
    case 'left':
    default:
      top = rect.top + rect.height / 2 - TOOLTIP_H / 2;
      left = rect.left - PAD - pad - TOOLTIP_W;
      break;
  }

  // clamp to viewport
  left = Math.max(12, Math.min(left, vw - TOOLTIP_W - 12));
  top = Math.max(12, Math.min(top, vh - TOOLTIP_H - 12));

  const isLast = currentIndex === totalSteps - 1;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top,
        left,
        width: TOOLTIP_W,
        background: 'var(--bg-primary)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        padding: '16px 18px',
        boxShadow: '0 16px 40px -8px rgba(0,0,0,0.32)',
        zIndex: 2,
        pointerEvents: 'auto',
      }}
    >
      {/* step counter */}
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent)', marginBottom: 6 }}>
        {currentIndex + 1} of {totalSteps}
      </div>

      {/* title */}
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
        {step.title}
      </div>

      {/* body */}
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55, marginBottom: 16 }}>
        {step.body}
      </div>

      {/* controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* eslint-disable-next-line no-restricted-syntax -- bespoke tour coach-mark control (ghost popover chrome); a one-off tour-overlay affordance, not a components/ui Button. Deferred per Track E. */}
        <button
          type="button"
          onClick={onClose}
          style={ghostBtn}
        >
          ✕ Close
        </button>
        <div style={{ display: 'flex', gap: 6 }}>
          {currentIndex > 0 && (
            // eslint-disable-next-line no-restricted-syntax -- bespoke tour coach-mark control (ghost popover chrome); a one-off tour-overlay affordance, not a components/ui Button. Deferred per Track E.
            <button type="button" onClick={onPrev} style={ghostBtn}>
              ← Back
            </button>
          )}
          {/* eslint-disable-next-line no-restricted-syntax -- bespoke tour coach-mark control (primary popover chrome); a one-off tour-overlay affordance, not a components/ui Button. Deferred per Track E. */}
          <button type="button" onClick={onNext} style={primaryBtn}>
            {isLast ? 'Finish ✓' : 'Next →'}
          </button>
        </div>
      </div>

      {/* progress dots */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 5, marginTop: 12 }}>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            key={i}
            style={{
              width: i === currentIndex ? 16 : 6,
              height: 6,
              borderRadius: 999,
              background: i === currentIndex ? 'var(--accent)' : 'var(--border)',
              transition: 'width 0.2s, background 0.2s',
            }}
          />
        ))}
      </div>
    </div>
  );
}

const base: React.CSSProperties = {
  padding: '5px 12px',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  border: '1px solid var(--border)',
  transition: 'background 0.15s',
};

const ghostBtn: React.CSSProperties = {
  ...base,
  background: 'transparent',
  color: 'var(--text-muted)',
};

const primaryBtn: React.CSSProperties = {
  ...base,
  background: 'var(--accent)',
  color: '#fff',
  border: '1px solid var(--accent)',
};
